/**
 * Shared types for resource monitoring.
 *
 * These types are used in both the resource-monitoring package and the emails package.
 */

export const MonitorOperationType = {
  Delete: "delete",
  Disable: "disable",
  Enable: "enable",
  Evaluate: "evaluate",
  EvaluateAll: "evaluateAll",
} as const;

export type MonitorOperationType =
  (typeof MonitorOperationType)[keyof typeof MonitorOperationType];

export enum MonitoredResourceType {
  Sync = "Sync",
  EventSource = "EventSource",
  EventSync = "EventSync",
  Journey = "Journey",
  IDR = "IDR",
  EventStreamingSync = "EventStreamingSync",
  DecisionEngineFlow = "DecisionEngineFlow",
}

export const MonitoredResourceTypeFriendlyNames: {
  [M in MonitoredResourceType]: string;
} = {
  [MonitoredResourceType.Sync]: "Sync",
  [MonitoredResourceType.EventSource]: "Event source",
  [MonitoredResourceType.EventSync]: "Event sync",
  [MonitoredResourceType.Journey]: "Journey",
  [MonitoredResourceType.IDR]: "IDR graph",
  [MonitoredResourceType.EventStreamingSync]: "Event streaming sync",
  [MonitoredResourceType.DecisionEngineFlow]: "AI Decisioning agent",
};

export enum MonitorConditionTypes {
  SyncRejectedRows = "SyncRejectedRows",
  SyncRejectedRowsV2 = "SyncRejectedRowsV2",
  SyncSequentialFailures = "SyncSequentialFailures",
  SyncDuration = "SyncDuration",
  SyncMissedSchedules = "SyncMissedSchedules",
  SyncThroughput = "SyncThroughput",
  EventSourceVolume = "EventSourceVolume",
  ModelSize = "ModelSize",
  JourneySequentialFailures = "JourneySequentialFailures",
  IDRRunFailure = "IDRRunFailure",
  EventStreamingSyncLatency = "EventStreamingSyncLatency",
  EventStreamingSyncFailures = "EventStreamingSyncFailures",
  AIDRunError = "AIDRunError",
  AIDRunDuration = "AIDRunDuration",
  AIDIdleTime = "AIDIdleTime",
  AIDLowInteractionVolume = "AIDLowInteractionVolume",
  EventSourceLag = "EventSourceLag",
  JourneySyncUnaccountedVolume = "JourneySyncUnaccountedVolume",
}

export const ConditionFriendlyNames: {
  [M in MonitorConditionTypes]: string;
} = {
  [MonitorConditionTypes.SyncSequentialFailures]: "Fatal errors",
  [MonitorConditionTypes.SyncRejectedRows]: "Rejected rows",
  [MonitorConditionTypes.SyncRejectedRowsV2]: "Rejected rows",
  [MonitorConditionTypes.SyncThroughput]: "Sync throughput",
  [MonitorConditionTypes.ModelSize]: "Model or audience size",
  [MonitorConditionTypes.SyncDuration]: "Sync duration",
  [MonitorConditionTypes.SyncMissedSchedules]: "Excessive idle time",
  [MonitorConditionTypes.EventSourceVolume]: "Volume",
  [MonitorConditionTypes.JourneySequentialFailures]: "Fatal errors",
  [MonitorConditionTypes.IDRRunFailure]: "IDR run failure",
  [MonitorConditionTypes.EventStreamingSyncLatency]: "Latency",
  [MonitorConditionTypes.EventStreamingSyncFailures]: "Failures",
  [MonitorConditionTypes.EventSourceLag]: "Queue backlog",
  [MonitorConditionTypes.AIDRunError]: "Agent run failure",
  [MonitorConditionTypes.AIDRunDuration]: "Agent run duration",
  [MonitorConditionTypes.AIDIdleTime]: "Agent idle time",
  [MonitorConditionTypes.AIDLowInteractionVolume]:
    "Low number of recommendations generated",
  [MonitorConditionTypes.JourneySyncUnaccountedVolume]:
    "Unexpected sync volume",
};

export enum CriticalEffect {
  Reports = "critical",
  StopsRun = "stop",
}

export const ConditionCriticalEffects: {
  [M in MonitorConditionTypes]: CriticalEffect;
} = {
  [MonitorConditionTypes.SyncSequentialFailures]: CriticalEffect.Reports,
  [MonitorConditionTypes.SyncRejectedRows]: CriticalEffect.Reports,
  [MonitorConditionTypes.SyncRejectedRowsV2]: CriticalEffect.Reports,
  [MonitorConditionTypes.SyncThroughput]: CriticalEffect.Reports,
  [MonitorConditionTypes.ModelSize]: CriticalEffect.Reports,
  [MonitorConditionTypes.SyncDuration]: CriticalEffect.Reports,
  [MonitorConditionTypes.SyncMissedSchedules]: CriticalEffect.Reports,
  [MonitorConditionTypes.EventSourceVolume]: CriticalEffect.Reports,
  [MonitorConditionTypes.JourneySequentialFailures]: CriticalEffect.Reports,
  [MonitorConditionTypes.IDRRunFailure]: CriticalEffect.Reports,
  [MonitorConditionTypes.EventStreamingSyncLatency]: CriticalEffect.Reports,
  [MonitorConditionTypes.EventStreamingSyncFailures]: CriticalEffect.Reports,
  [MonitorConditionTypes.EventSourceLag]: CriticalEffect.Reports,
  [MonitorConditionTypes.AIDRunError]: CriticalEffect.Reports,
  [MonitorConditionTypes.AIDRunDuration]: CriticalEffect.Reports,
  [MonitorConditionTypes.AIDIdleTime]: CriticalEffect.Reports,
  [MonitorConditionTypes.AIDLowInteractionVolume]: CriticalEffect.Reports,
  [MonitorConditionTypes.JourneySyncUnaccountedVolume]: CriticalEffect.StopsRun,
};

export const isMonitorConditionType = (e): e is MonitorConditionTypes =>
  Object.values(MonitorConditionTypes).includes(e);

export const TriggerDescriptions = Object.fromEntries(
  Object.entries(ConditionFriendlyNames).map(([k, v]) => [k, v + " trigger"]),
);

export enum MonitorStatus {
  Unhealthy = "unhealthy",
  Warning = "warning",
  Healthy = "healthy",
  Disabled = "disabled",
}

export const MonitorStatusNames = {
  [MonitorStatus.Healthy]: "healthy",
  [MonitorStatus.Warning]: "warning",
  [MonitorStatus.Unhealthy]: "critical",
  [MonitorStatus.Disabled]: "disabled",
} as const;

export const HealthDescriptions: Record<MonitorStatus, string> = {
  [MonitorStatus.Healthy]: "has recovered",
  [MonitorStatus.Warning]: "has a warning alert",
  [MonitorStatus.Unhealthy]: "has a critical alert",
  [MonitorStatus.Disabled]: "is disabled",
};

export type StatusUpdate = {
  oldStatus: MonitorStatus | null;
  newStatus: MonitorStatus;
  statusChangeId: string;
  statusChangeTime: string;
};

/**
 * User-defined labels on a resource. Stored as the `tags` jsonb column in
 * Postgres; "labels" is the name the product and the permissions layer use.
 */
export type ResourceLabels = Record<string, string>;

export type SyncAlertMetadata = {
  type: MonitoredResourceType.Sync;
  destination: {
    id: string;
    name: string;
    type: string;
    icon: string;
    labels: ResourceLabels;
  };
  model: {
    id: string;
    name: string;
    icon: string;
    type: string;
  };
  labels: ResourceLabels;
  updatedBy?: string;
  updatedAt?: Date;
  createdBy?: string;
  createdAt: Date;
  resourceDescription?: string | null;
  workspace: {
    name: string;
    slug: string;
    id: string;
  };
};

export type EvaluatedConditionStatus = {
  condition_id?: string;
  condition_template_id?: string;
  condition_type?: MonitorConditionTypes;
  evaluated_at: Date;
  event_time: Date;
  current_status: MonitorStatus;
  state: {
    input: unknown;
    threshold: unknown;
    value: unknown;
    condition_type?: MonitorConditionTypes;
    resourceError?: string;
  };
  id: string;
  mute_until?: Date;
};

export type MonitorConditionToChannelTemplate = (opts: {
  context: unknown;
  condition: EvaluatedConditionStatus;
}) => Promise<{
  text: string;
  title?: string;
  metadata?: { error?: string };
} | null>;
